// Gate URL helpers.
//
// V1 canonical routing is PATH-BASED — every Gate lives at
// `gateandgather.co.nz/g/{slug}`. Wildcard subdomain TLS isn't available on
// the current deployment plan, so path routing is the permanent public URL
// structure. `subdomainSlug()` is retained purely so that legacy
// `{slug}.gateandgather.co.nz` links still resolve (via a client-side
// redirect in App.js) — slugs remain preserved so we can move back to
// wildcard subdomain hosting later if infrastructure supports it.

const APEX_HOSTS = new Set([
  "gateandgather.co.nz",
  "www.gateandgather.co.nz",
  "localhost",
  "127.0.0.1",
]);

// The public apex we always canonicalise to when we need an absolute URL
// (share links, market signs, QR codes, emails, canonical tags).
const CANONICAL_APEX = "https://www.gateandgather.co.nz";

// Treat the Emergent preview host from REACT_APP_BACKEND_URL as apex too —
// this lets the preview URL keep serving the landing + /g/{slug} pattern.
function previewApex() {
  try {
    const u = new URL(process.env.REACT_APP_BACKEND_URL || "");
    return u.hostname;
  } catch { return null; }
}

export function currentHost() {
  if (typeof window === "undefined") return "";
  return window.location.hostname.toLowerCase();
}

export function isApexHost(host = currentHost()) {
  if (APEX_HOSTS.has(host)) return true;
  const p = previewApex();
  return !!p && host === p.toLowerCase();
}

// Returns the slug when we're on a *.gateandgather.co.nz subdomain, else null.
// Only used to redirect legacy subdomain traffic to the canonical /g/{slug} URL.
export function subdomainSlug(host = currentHost()) {
  if (isApexHost(host)) return null;
  if (host.endsWith(".gateandgather.co.nz")) {
    const label = host.slice(0, -".gateandgather.co.nz".length);
    if (label && label !== "www") return label;
  }
  return null;
}

// Relative in-app path — always /g/{slug}. Used for <Link to=…> targets.
export function gatePath(slug) {
  return slug ? `/g/${slug}` : "/";
}

// Build the customer-facing absolute URL for a Gate slug.
// - Production apex (gateandgather.co.nz / www): `https://www.gateandgather.co.nz/g/{slug}`
// - Anywhere else (preview / localhost / legacy subdomain): current origin + /g/{slug}
export function gateUrl(slug) {
  if (!slug) return "/";
  const host = currentHost();
  if (host === "gateandgather.co.nz" || host === "www.gateandgather.co.nz") {
    return `${CANONICAL_APEX}/g/${slug}`;
  }
  if (typeof window !== "undefined") {
    return `${window.location.origin}/g/${slug}`;
  }
  return `${CANONICAL_APEX}/g/${slug}`;
}

// Display string for settings ("your Gate lives at …").
export function gateUrlDisplay(slug) {
  return `gateandgather.co.nz/g/${slug}`;
}
